import type { FilterOp } from '@servicetim/shared';
import { Button } from '@servicetim/ui/components';
import { Trash2 } from 'lucide-react';
import type { CatalogField, FieldCatalog } from '../catalog/field-catalog';
import { type SelectOption, SimpleSelect } from '../ui/simple-select';
import { carryValue, OP_LABELS, opsForType } from './ops';
import { ConditionValueControl } from './value-control';

/** `{ field, op, value }`: the shape shared by query leaves, `field_matches` rules and `visibleWhen`. */
export interface FieldCondition {
  field: string;
  op: FilterOp;
  value?: unknown;
}

export interface FieldConditionRowProps {
  value: FieldCondition;
  onChange: (next: FieldCondition) => void;
  catalog: FieldCatalog;
  /** Restricts the selectable fields (for example to fields placed on a form). */
  fieldFilter?: (field: CatalogField) => boolean;
  disabled?: boolean;
  idPrefix: string;
  allowMe?: boolean;
  onRemove?: () => void;
}

export function fieldOptions(catalog: FieldCatalog, filter?: (f: CatalogField) => boolean) {
  const options: SelectOption[] = [];
  for (const f of catalog.system) {
    if (!filter || filter(f)) options.push({ value: f.key, label: f.label, group: 'System' });
  }
  for (const f of catalog.custom) {
    if (!filter || filter(f)) {
      options.push({
        value: f.key,
        label: f.archived ? `${f.label} (archived)` : f.label,
        group: 'Custom fields',
        disabled: f.archived,
      });
    }
  }
  return options;
}

/** Field select, operator select (per field type) and the matching value control. */
export function FieldConditionRow({
  value,
  onChange,
  catalog,
  fieldFilter,
  disabled,
  idPrefix,
  allowMe = true,
  onRemove,
}: FieldConditionRowProps) {
  const field = catalog.byKey.get(value.field);
  const ops = opsForType(field?.type);
  return (
    <div className="flex flex-wrap items-start gap-2" data-slot="field-condition">
      <SimpleSelect
        id={`${idPrefix}-field`}
        aria-label="Field"
        value={value.field || undefined}
        placeholder="Field…"
        options={fieldOptions(catalog, fieldFilter)}
        disabled={disabled}
        className="w-52"
        onChange={(nextKey) => {
          if (!nextKey) return;
          const nextField = catalog.byKey.get(nextKey);
          const allowed = opsForType(nextField?.type);
          const op = allowed.includes(value.op) ? value.op : (allowed[0] ?? 'eq');
          onChange({ field: nextKey, op, value: undefined });
        }}
      />
      <SimpleSelect
        id={`${idPrefix}-op`}
        aria-label="Operator"
        value={value.op}
        options={ops.map((op) => ({ value: op, label: OP_LABELS[op] }))}
        disabled={disabled || !field}
        className="w-40"
        onChange={(nextOp) => {
          if (!nextOp) return;
          const op = nextOp as FilterOp;
          onChange({ field: value.field, op, value: carryValue(value.op, op, value.value) });
        }}
      />
      <div className="min-w-48 flex-1">
        <ConditionValueControl
          id={`${idPrefix}-value`}
          field={field}
          op={value.op}
          value={value.value}
          disabled={disabled}
          allowMe={allowMe}
          onChange={(next) => onChange({ ...value, value: next })}
        />
      </div>
      {onRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Remove condition"
          onClick={onRemove}
          disabled={disabled}
        >
          <Trash2 />
        </Button>
      ) : null}
    </div>
  );
}
